import { resetPasswordRequestSchema } from "@opendialysis/procare-api-schemas";

import { apiClient } from "../../../lib/apiClient";
import { API } from "../../../constants/apiRoutes";

/** Always resolves — the endpoint answers 202 whether or not the account exists. */
export async function requestPasswordReset(email: string): Promise<void> {
  const body = resetPasswordRequestSchema.body.parse({ email });
  await apiClient.post(API.auth.resetPassword, body, { skipAuth: true });
}

/**
 * Completes account activation or a password reset.
 *
 * skipAuth is load-bearing: PUT /users/password branches on the presence of an
 * Authorization header, and with one it changes the SIGNED-IN user's password
 * instead of honouring the emailed token. All three fields must be sent — the
 * body is a zod union whose reset-token member has to match first, and a
 * partial body silently falls through to the authenticated shape.
 */
export async function setPasswordWithToken(input: {
  userId: string;
  token: string;
  newPassword: string;
}): Promise<void> {
  await apiClient.put(API.users.password, input, { skipAuth: true });
}

/**
 * Asks whether an emailed link is still usable, before offering the form.
 *
 * Resolves when it is and REJECTS with the normal error shape when it is not,
 * so the caller renders the same message it would have shown after a refused
 * submit. The token is not spent either way.
 */
export async function verifyPasswordToken(input: {
  userId: string;
  token: string;
}): Promise<void> {
  await apiClient.post(API.users.verifyPasswordToken, input, {
    skipAuth: true,
  });
}

/**
 * Changes the caller's own password. The server deletes every refresh token
 * for that user, so this ends the CURRENT session too — callers must sign out
 * deliberately rather than letting the next 401 surface as "session expired".
 *
 * `currentPassword` re-authenticates: a live session alone must not be enough
 * to take the account over. A wrong one answers 400 INVALID_CURRENT_PASSWORD,
 * never 401 — a 401 would send the interceptor off to refresh and sign the
 * user out over a typo.
 */
export async function changeOwnPassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<void> {
  await apiClient.put(API.users.password, input);
}
